/**
 * CB Application Review — admin queue for reviewing applications.
 * Route: /admin/applications  (super_admin only)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../api/axiosClient';
import {
  PageHeader, Card, Btn, DataTable, Alert, StandardModal,
  colors, spacing, typography, radius,
  inputStyle, textareaStyle, selectStyle,
} from '../design-system';

// ── Constants ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  draft:                    { label: 'Draft',                color: '#64748b' },
  submitted:                { label: 'Submitted',            color: '#0ea5e9' },
  under_review:             { label: 'Under Review',         color: '#8b5cf6' },
  additional_info_required: { label: 'Info Required',        color: '#f59e0b' },
  assessment_scheduled:     { label: 'Assessment Scheduled', color: '#06b6d4' },
  approved:                 { label: 'Approved',             color: '#10b981' },
  rejected:                 { label: 'Rejected',             color: '#ef4444' },
  withdrawn:                { label: 'Withdrawn',            color: '#94a3b8' },
};

const STATUS_TRANSITIONS = [
  { value: 'under_review',             label: 'Move to Under Review' },
  { value: 'additional_info_required', label: 'Request Additional Info' },
  { value: 'assessment_scheduled',     label: 'Schedule Assessment' },
  { value: 'approved',                 label: 'Approve' },
  { value: 'rejected',                 label: 'Reject' },
];

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#64748b' };
  return (
    <span style={{
      background: cfg.color + '18',
      color: cfg.color,
      border: `1px solid ${cfg.color}30`,
      borderRadius: radius.pill,
      fontSize: typography.fontSizes.xs,
      fontWeight: typography.fontWeights.bold,
      padding: '2px 8px',
    }}>
      {cfg.label}
    </span>
  );
}

function FieldLabel({ children }) {
  return (
    <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: 6 }}>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: spacing.sm, padding: '5px 0', borderBottom: `1px solid ${colors.border}`, fontSize: typography.fontSizes.sm }}>
      <span style={{ color: colors.textMuted, minWidth: 140, flexShrink: 0 }}>{label}</span>
      <span style={{ color: colors.textPrimary, fontWeight: typography.fontWeights.medium, wordBreak: 'break-word' }}>{value || '—'}</span>
    </div>
  );
}

// ── Application Detail Drawer ──────────────────────────────────────────────────

function ApplicationDrawer({ app, onClose, onUpdated }) {
  const [tab,    setTab]    = useState('details');
  const [saving, setSaving] = useState(false);
  const [decision, setDecision] = useState({ status: '', review_notes: '', decision_notes: '', rejection_reason: '' });
  const [msgBody, setMsgBody]   = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [msg, setMsg] = useState(null);
  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 4000); };

  const makeDecision = async () => {
    if (!decision.status) return;
    setSaving(true);
    try {
      const res = await axiosClient.patch(`/cb-applications/${app.id}/decision`, decision);
      onUpdated(res.data);
      flash('Decision saved.');
      setDecision({ status: '', review_notes: '', decision_notes: '', rejection_reason: '' });
    } catch {
      flash('Failed to save decision.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const sendMessage = async () => {
    if (!msgBody.trim()) return;
    setSaving(true);
    try {
      await axiosClient.post(`/cb-applications/${app.id}/messages`, { body: msgBody, is_internal: isInternal });
      const res = await axiosClient.get(`/cb-applications/${app.id}`);
      onUpdated(res.data);
      setMsgBody('');
      flash('Message sent.');
    } catch {
      flash('Failed to send message.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { key: 'details',  label: 'Details' },
    { key: 'decision', label: 'Decision' },
    { key: 'messages', label: `Messages (${(app.messages || []).length})` },
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end',
      background: 'rgba(0,0,0,0.4)',
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 660, background: colors.surface, height: '100%', overflowY: 'auto', boxShadow: '-8px 0 40px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ padding: `${spacing.lg} ${spacing.lg} ${spacing.md}`, borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing.xs }}>
            <div style={{ fontFamily: 'monospace', fontSize: typography.fontSizes.sm, color: colors.textMuted }}>{app.reference_number}</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, fontSize: 20 }}>×</button>
          </div>
          <div style={{ fontSize: typography.fontSizes.xl, fontWeight: typography.fontWeights.bold, color: colors.textPrimary, marginBottom: spacing.sm }}>{app.company_legal_name}</div>
          <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
            <StatusBadge status={app.status} />
            {app.contact_name && <span style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>{app.contact_name}</span>}
          </div>
        </div>

        {msg && <div style={{ padding: `${spacing.xs} ${spacing.lg}` }}><Alert type={msg.type} message={msg.text} /></div>}

        <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}`, padding: `0 ${spacing.lg}` }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: `${spacing.sm} ${spacing.md}`, background: 'none', border: 'none',
              borderBottom: tab === t.key ? `2px solid ${colors.primary}` : '2px solid transparent',
              marginBottom: -1, cursor: 'pointer',
              fontSize: typography.fontSizes.sm,
              fontWeight: tab === t.key ? typography.fontWeights.semibold : typography.fontWeights.medium,
              color: tab === t.key ? colors.primary : colors.textSecondary,
            }}>{t.label}</button>
          ))}
        </div>

        <div style={{ flex: 1, padding: spacing.lg, overflowY: 'auto' }}>
          {tab === 'details' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
              <div>
                <FieldLabel>Company Information</FieldLabel>
                <InfoRow label="Legal Name"     value={app.company_legal_name} />
                <InfoRow label="Trading Name"   value={app.trading_name} />
                <InfoRow label="Address"        value={app.company_address} />
                <InfoRow label="Website"        value={app.company_website} />
                <InfoRow label="Phone"          value={app.company_phone} />
              </div>
              <div>
                <FieldLabel>Contact</FieldLabel>
                <InfoRow label="Name"      value={app.contact_name} />
                <InfoRow label="Email"     value={app.contact_email} />
                <InfoRow label="Job Title" value={app.contact_job_title} />
              </div>
              <div>
                <FieldLabel>Scope</FieldLabel>
                <InfoRow label="Standards"   value={(app.standards_applied_for || []).join(', ')} />
                <InfoRow label="Description" value={app.scope_description} />
                <InfoRow label="Geography"   value={app.geographic_scope} />
                <InfoRow label="Years operating" value={app.years_in_operation} />
                <InfoRow label="Staff count" value={app.num_staff} />
                <InfoRow label="Existing accreditations" value={app.existing_accreditations} />
              </div>
              {app.documents && app.documents.length > 0 && (
                <div>
                  <FieldLabel>Documents</FieldLabel>
                  {app.documents.map((d, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${colors.border}`, fontSize: typography.fontSizes.sm }}>
                      <span style={{ color: colors.textPrimary }}>{d.name}</span>
                      {d.url && <a href={d.url} target="_blank" rel="noreferrer" style={{ color: colors.primary }}>View</a>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'decision' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              <div>
                <FieldLabel>Action</FieldLabel>
                <select style={selectStyle} value={decision.status} onChange={e => setDecision(d => ({ ...d, status: e.target.value }))}>
                  <option value="">Select action…</option>
                  {STATUS_TRANSITIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel>Internal Review Notes</FieldLabel>
                <textarea style={{ ...textareaStyle, minHeight: 60 }} value={decision.review_notes} onChange={e => setDecision(d => ({ ...d, review_notes: e.target.value }))} placeholder="Internal notes (not visible to applicant)…" />
              </div>
              <div>
                <FieldLabel>Decision Notes (visible to applicant)</FieldLabel>
                <textarea style={{ ...textareaStyle, minHeight: 60 }} value={decision.decision_notes} onChange={e => setDecision(d => ({ ...d, decision_notes: e.target.value }))} />
              </div>
              {decision.status === 'rejected' && (
                <div>
                  <FieldLabel>Rejection Reason</FieldLabel>
                  <textarea style={{ ...textareaStyle, minHeight: 60 }} value={decision.rejection_reason} onChange={e => setDecision(d => ({ ...d, rejection_reason: e.target.value }))} />
                </div>
              )}
              <Btn variant="primary" size="md" onClick={makeDecision} loading={saving} disabled={!decision.status}>
                Save Decision
              </Btn>
            </div>
          )}

          {tab === 'messages' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {(!app.messages || !app.messages.length) && (
                  <div style={{ color: colors.textMuted, fontSize: typography.fontSizes.sm }}>No messages yet.</div>
                )}
                {(app.messages || []).map(m => (
                  <div key={m.id} style={{ marginBottom: spacing.sm, padding: spacing.sm, background: m.is_internal ? '#fef9c3' : colors.background, borderRadius: radius.md, border: `1px solid ${m.is_internal ? '#fde047' : colors.border}` }}>
                    <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, color: colors.textMuted, marginBottom: 2 }}>
                      {m.author_name} {m.is_internal ? '(internal)' : ''} · {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                    </div>
                    <div style={{ fontSize: typography.fontSizes.sm, color: colors.textPrimary }}>{m.body}</div>
                  </div>
                ))}
              </div>
              <div>
                <textarea style={{ ...textareaStyle, minHeight: 60, marginBottom: spacing.sm }} value={msgBody} onChange={e => setMsgBody(e.target.value)} placeholder="Write a message to the applicant…" />
                <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, fontSize: typography.fontSizes.sm, cursor: 'pointer' }}>
                    <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} />
                    Internal only
                  </label>
                  <Btn variant="primary" size="sm" onClick={sendMessage} loading={saving} disabled={!msgBody.trim()}>Send</Btn>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

const COLS = [
  { key: 'reference_number', label: 'Reference', render: v => (
    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: colors.primary }}>{v}</span>
  )},
  { key: 'company_legal_name', label: 'Company' },
  { key: 'standards_applied_for', label: 'Standards', render: v => (v || []).join(', ') || '—' },
  { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
  { key: 'submitted_at', label: 'Submitted', render: v => v ? new Date(v).toLocaleDateString() : '—' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'all',       label: 'All' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'additional_info_required', label: 'Info Required' },
  { value: 'assessment_scheduled',     label: 'Scheduled' },
  { value: 'approved',  label: 'Approved' },
  { value: 'rejected',  label: 'Rejected' },
];

export default function CBApplicationReview() {
  const [apps,     setApps]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('all');
  const [selected, setSelected] = useState(null);
  const [msg,      setMsg]      = useState(null);
  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axiosClient.get(`/cb-applications/${params}`);
      setApps(res.data);
    } catch {
      flash('Failed to load applications.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const stats = {
    pending:  apps.filter(a => ['submitted', 'under_review', 'additional_info_required', 'assessment_scheduled'].includes(a.status)).length,
    approved: apps.filter(a => a.status === 'approved').length,
    rejected: apps.filter(a => a.status === 'rejected').length,
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: spacing.lg }}>
      <PageHeader title="Application Review" subtitle="Review and process CB accreditation applications" />

      {msg && <Alert type={msg.type} message={msg.text} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: spacing.md, marginBottom: spacing.lg }}>
        {[
          { label: 'Awaiting Action', value: stats.pending,  color: '#0ea5e9' },
          { label: 'Approved',        value: stats.approved, color: '#10b981' },
          { label: 'Rejected',        value: stats.rejected, color: '#ef4444' },
        ].map(k => (
          <Card key={k.label} style={{ borderTop: `3px solid ${k.color}` }}>
            <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: colors.textMuted }}>{k.label}</div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: k.color, lineHeight: 1.1, marginTop: 4 }}>{k.value}</div>
          </Card>
        ))}
      </div>

      <div style={{ display: 'flex', gap: spacing.xs, flexWrap: 'wrap', marginBottom: spacing.md }}>
        {STATUS_FILTER_OPTIONS.map(f => (
          <button key={f.value} onClick={() => setFilter(f.value)} style={{
            padding: '5px 14px', borderRadius: radius.pill,
            border: `1px solid ${filter === f.value ? colors.primary : colors.border}`,
            background: filter === f.value ? colors.primary : colors.surface,
            color: filter === f.value ? '#fff' : colors.textSecondary,
            cursor: 'pointer', fontSize: typography.fontSizes.sm,
            fontWeight: filter === f.value ? typography.fontWeights.semibold : typography.fontWeights.medium,
            transition: 'all 120ms',
          }}>{f.label}</button>
        ))}
      </div>

      <Card>
        <DataTable
          columns={[...COLS, { key: '_action', label: '', render: (_, row) => (
            <Btn variant="ghost" size="sm" onClick={() => setSelected(row)}>Review</Btn>
          )}]}
          rows={apps}
          keyField="id"
          loading={loading}
          emptyMessage="No applications found."
        />
      </Card>

      {selected && (
        <ApplicationDrawer
          app={selected}
          onClose={() => setSelected(null)}
          onUpdated={updated => {
            setApps(prev => prev.map(a => a.id === updated.id ? updated : a));
            setSelected(updated);
          }}
        />
      )}
    </div>
  );
}
